import { FC, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button, Input, InputMask } from 'components/ui'
import { useNotification } from 'hooks/use-notification'
import type { ChangeDataProps } from '.'
import s from './change-password.module.css'

const ChangeUserData: FC<ChangeDataProps> = ({ onSubmit, data }) => {
  const { notify } = useNotification()
  const {
    register,
    handleSubmit,
    formState: { errors, touched, isSubmitting },
    setError,
    clearErrors,
  } = useForm({
    mode: 'onChange',
  })

  const [isExemptState, setIsExemptState] = useState(data.isExemptState === 1)

  const handleChangeExemptState = () => {
    setIsExemptState(!isExemptState)
    clearErrors()
  }

  const handleFormSubmit = ({
    email,
    name,
    phone,
    birthday,
    fantasy_name,
    name_company,
    ins_estadual,
  }: {
    email: string
    phone: string
    name: string
    birthday: string
    fantasy_name: string
    name_company: string
    ins_estadual: string
  }) => {
    const userName = name.trim()
    if (userName !== data.name) {
      if (!userName.includes(' ')) {
        setError('name', {
          message: `Informe ${data.isPerson ? 'o nome completo' : 'a razão social'}`,
        })
        return
      }
    }

    try {
      if (birthday && birthday !== '') {
        const reg = new RegExp(
          '^(((0[1-9]|[12][0-9]|30)[-/]?(0[13-9]|1[012])|31[-/]?(0[13578]|1[02])|(0[1-9]|1[0-9]|2[0-8])[-/]?02)[-/]?[0-9]{4}|29[-/]?02[-/]?([0-9]{2}(([2468][048]|[02468][48])|[13579][26])|([13579][26]|[02468][048]|0[0-9]|1[0-6])00))$',
        )
        const text = birthday.split('/').join('')

        if (!reg.test(text)) {
          throw new Error('Data inválida')
        }
      }
    } catch (e) {
      notify({
        type: 'danger',
        title: 'Data de nascimento inválida',
      })
      return
    }

    onSubmit(email, name, phone, birthday, fantasy_name, name_company, ins_estadual)
  }

  return (
    <div className={s.root}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Input
          type="text"
          name="name"
          label={data.isPerson ? 'Seu nome' : 'Sua razão social'}
          placeholder="Digite aqui"
          error={errors?.name?.message}
          touched={touched?.name}
          maxLength={72}
          disabled={isSubmitting}
          defaultValue={data.name}
          ref={register({
            required: {
              value: true,
              message: `Digite ${data.isPerson ? 'seu nome' : 'sua razão social'}`,
            },
            minLength: {
              value: 6,
              message: 'No mínimo 6 (seis) caracteres',
            },
          })}
        />
        {!data.isPerson && (
          <>
            <Input
              type="text"
              name="fantasy_name"
              label="Seu nome fantasia"
              placeholder="Digite aqui"
              error={errors?.fantasy_name?.message}
              touched={touched?.fantasy_name}
              maxLength={85}
              disabled={isSubmitting}
              defaultValue={data.fantasyName}
              ref={register({
                required: {
                  value: true,
                  message: 'Digite seu nome fantasia',
                },
                minLength: {
                  value: 6,
                  message: 'No mínimo 6 (seis) caracteres',
                },
              })}
            />
            <Input
              type="text"
              name="name_company"
              label="Nome responsável"
              placeholder="Digite aqui"
              error={errors?.name_company?.message}
              touched={touched?.name_company}
              maxLength={140}
              disabled={isSubmitting}
              defaultValue={data.company_name}
              ref={register({
                required: {
                  value: true,
                  message: 'Digite o nome do responsável pela empresa',
                },
                minLength: {
                  value: 6,
                  message: 'No mínimo 6 (seis) caracteres',
                },
              })}
            />
            <div className={s.labelCheckBox}>
              <label htmlFor="iptInscricaoEstadual" className={s.label}>
                <input
                  type="checkbox"
                  disabled={data.isExemptState === 0}
                  name="iptInscricaoEstadual"
                  checked={isExemptState}
                  onChange={handleChangeExemptState}
                />
                Isento de inscrição estadual
              </label>
            </div>
            {!isExemptState && (
              <Input
                type="text"
                name="ins_estadual"
                label="Sua inscrição estadual"
                placeholder="Digite aqui"
                error={errors?.ins_estadual?.message}
                touched={touched?.ins_estadual}
                maxLength={45}
                disabled={isSubmitting}
                defaultValue={data.insEstadual}
                ref={register({
                  required: {
                    value: true,
                    message: 'Digite Sua inscrição estadual',
                  },
                  minLength: {
                    value: 5,
                    message: 'No mínimo 6 (seis) caracteres',
                  },
                })}
              />
            )}
          </>
        )}

        <Input
          type="email"
          name="email"
          label="Seu e-mail"
          placeholder="Seu e-mail"
          error={errors?.email?.message}
          touched={touched?.email}
          defaultValue={data.email}
          maxLength={72}
          disabled={isSubmitting}
          ref={register({
            required: {
              value: true,
              message: 'Digite seu nome',
            },
            minLength: {
              value: 6,
              message: 'No mínimo 6 (seis) caracteres',
            },
          })}
        />

        <InputMask
          mask="(99) 99999-9999"
          type="tel"
          name="phone"
          label="Seu número de celular"
          placeholder="(DD) 99999-9999"
          error={errors?.phone?.message}
          touched={touched?.phone}
          defaultValue={data.phone}
          ref={register({
            required: {
              value: true,
              message: 'Por favor, preencha seu número de celular',
            },
          })}
        />

        {data.isPerson && (
          <InputMask
            mask="99/99/9999"
            type="text"
            name="birthday"
            label="Data de nascimento"
            placeholder="DD/MM/AAAA"
            error={errors?.birthday?.message}
            touched={touched?.birthday}
            defaultValue={data.birthday ? data.birthday.replace(/[^0-9]+/g, '') : ''}
            ref={register({
              required: {
                value: true,
                message: 'Por favor, preencha com sua data de nascimento',
              },
            })}
          />
        )}

        <Button variant="primary" type="submit" loading={isSubmitting}>
          Atualizar
        </Button>
      </form>
    </div>
  )
}

export default ChangeUserData
